import { ConnectDialog } from "@/components/android-instances/ConnectDialog";
import { StatusBadge } from "@/components/android-instances/StatusBadge";
import { formatDateTime, formatDuration } from "@/utils/format";
import {
  Loader,
  Terminal,
  Trash2,
  Gamepad2,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSWRConfig } from "swr";

import { logout } from "@/auth/auth";
import { useUser } from "@/client/backend/user.tsx";
import { useAndroidInstances, useIosInstances } from "@/client/director/instances.tsx";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { SettingsLinkProps } from "@/routes/settings.tsx";
import { Sidebar } from "@/components/layout/Sidebar";
import { useOrganization } from "@/client/backend/organization.tsx";
import { useAuth } from "@/auth/auth";

export function Instances() {
  const auth = useAuth();
  const [selectedOS, setSelectedOS] = useState<'Android' | 'iOS'>('Android');
  const [stateFilter, setStateFilter] = useState<
    string[] | "all" | undefined
  >(["ready", "assigned"]);
  const { user, error: userError } = useUser();
  if (userError) {
    console.log({ userError });
  }
  const [organization, setOrganization] = useOrganization();

  const { data: androidData, error: androidError } = useAndroidInstances({
    states: stateFilter,
    enabled: selectedOS === 'Android',
  });
  const { data: iosData, error: iosError } = useIosInstances({
    states: stateFilter,
    enabled: selectedOS === 'iOS',
  });
  
  const instancesData = selectedOS === 'Android' ? androidData : iosData;
  const instancesError = selectedOS === 'Android' ? androidError : iosError;
  
  if (instancesError) {
    console.log({ instancesError });
  }
  
  // State for infinite scroll
  const [allInstances, setAllInstances] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageRef = useRef(instancesData);

  // Update all instances when initial data loads or filter changes
  useEffect(() => {
    if (instancesData !== "loading") {
      setAllInstances(instancesData.getPaginatedItems());
      pageRef.current = instancesData;
    }
  }, [instancesData, stateFilter, selectedOS]);

  // Function to load more instances
  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    
    const currentPage = pageRef.current;
    if (currentPage !== "loading" && currentPage.hasNextPage()) {
      setIsLoadingMore(true);
      try {
        const nextPage = await currentPage.getNextPage();
        pageRef.current = nextPage;
        setAllInstances(prev => [...prev, ...nextPage.getPaginatedItems()]);
      } catch (error) {
        console.error("Failed to load more instances:", error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [isLoadingMore]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when scrolled 80% down
      if (scrollPercentage > 0.8) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  const [deletingInstances, setDeletingInstances] = useState<Set<string>>(
    new Set(),
  );

  const { mutate } = useSWRConfig();

  const handleDelete = async (instanceId: string) => {
    try {
      setDeletingInstances((prev) => new Set(prev).add(instanceId));
      if (instanceId.startsWith('ios')) {
        await auth.regionClient.iosInstances.delete(instanceId);
        await mutate('iosInstances');
      } else {
        await auth.regionClient.androidInstances.delete(instanceId);
        await mutate('androidInstances');
      }
    } catch (error) {
      console.error("Failed to delete instance:", error);
      setDeletingInstances((prev) => {
        const next = new Set(prev);
        next.delete(instanceId);
        return next;
      });
    }
  };

  const [connectDialog, setConnectDialog] = useState<{
    isOpen: boolean;
    command?: string;
  }>({ isOpen: false });

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/40">
      <Sidebar />
      <div className="flex flex-col sm:gap-4 sm:py-4 sm:pl-14">
        <header className="sticky top-0 flex h-14 items-center gap-4 border-b bg-background px-4 sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6">
          <Breadcrumb className="hidden md:flex">
            <BreadcrumbList>
              <BreadcrumbItem>
                <Select
                  onValueChange={(value) => {
                    const selected =
                      user !== "loading"
                        ? user.organizations.find((org) => org.id === value)
                        : undefined;
                    if (selected) {
                      setOrganization(selected);
                    }
                  }}
                >
                  <SelectTrigger className="md:max-w-[300px] md:min-w-[240px]">
                    <SelectValue
                      placeholder={
                        organization === "loading"
                          ? "Loading..."
                          : organization.displayName
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Organizations</SelectLabel>
                      {(user !== "loading" ? user.organizations : []).map(
                        (org) => (
                          <SelectItem value={org.id} key={org.id}>
                            {org.displayName}
                          </SelectItem>
                        ),
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <Select
                  value={selectedOS}
                  onValueChange={(value: 'Android' | 'iOS') => setSelectedOS(value)}
                >
                  <SelectTrigger className="h-auto py-1 px-2 border-0 bg-transparent hover:bg-accent">
                    <SelectValue>
                      {selectedOS === 'iOS' ? 'iOS' : 'Android'} Instances
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="Android">Android Instances</SelectItem>
                      <SelectItem value="iOS">iOS Instances</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="overflow-hidden rounded-full"
              >
                <img
                  src="/placeholder-user.jpg"
                  width={36}
                  height={36}
                  alt="Avatar"
                  className="overflow-hidden rounded-full"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Link
                  to="/settings"
                  state={{ initialSection: "profile" } as SettingsLinkProps}
                  replace={true}
                >
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Link
                  to="/settings"
                  state={{ initialSection: "support" } as SettingsLinkProps}
                  replace={true}
                >
                  Support
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
          <Tabs
            defaultValue="running"
            onValueChange={(tab) => {
              switch (tab) {
                case "running":
                  setStateFilter(["ready,assigned"]);
                  break;
                case "terminated":
                  setStateFilter(["terminated"]);
                  break;
                case "all":
                  setStateFilter("all");
                  break;
                default:
                  setStateFilter(["unknown"]);
              }
            }}
          >
            <div className="flex items-center">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="running">Running</TabsTrigger>
                <TabsTrigger value="terminated" className="hidden sm:flex">
                  Terminated
                </TabsTrigger>
              </TabsList>
              {/* <div className="ml-auto flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        className="h-7 bg-blue-600 hover:bg-blue-700 text-white border-none shadow-sm"
                        onClick={async () => {
                          try {
                            setIsCreating(true);
                            const result = await auth.regionClient.androidInstances.create({ wait: true });
                            await mutate('androidInstances');
                            toast({
                              description: `${result.metadata.id} created successfully.`,
                            });
                          } catch (error: any) {
                            toast({
                              title: "Uh oh!",
                              description: formatErrorMessage(error.message ?? error.toString()),
                              variant: "destructive",
                            });
                          } finally {
                            setIsCreating(false);
                          }
                        }}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <Loader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <PlusCircle className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Create new instance</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div> */}
            </div>
            <Card x-chunk="dashboard-06-chunk-0">
              <CardHeader>
                <CardTitle>{selectedOS === 'iOS' ? 'iOS' : 'Android'} Instances</CardTitle>
                <CardDescription>
                  Manage your {selectedOS === 'iOS' ? 'iOS' : 'Android'} instances and view their status.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden md:table-cell">
                        Status
                      </TableHead>
                      {stateFilter?.includes("terminated") && (
                        <TableHead className="hidden md:table-cell">
                          Duration
                        </TableHead>
                      )}
                      <TableHead className="hidden md:table-cell">
                        Duration
                      </TableHead>
                      <TableHead className="hidden md:table-cell">
                        Created at
                      </TableHead>
                      <TableHead>
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allInstances.map((instance) => (
                      <TableRow key={instance.metadata.id}>
                        <TableCell>
                          {(deletingInstances.has(instance.metadata.id)) &&
                          instance.status?.state !== "ready" &&
                          instance.status?.state !== "terminated" ? (
                            <Button
                              size="sm"
                              className={cn(
                                "h-7 border disabled:opacity-100",
                                deletingInstances.has(instance.metadata.id)
                                  ? "bg-gray-50 border-gray-200"
                                  : "bg-blue-50 border-blue-200",
                              )}
                              disabled
                            >
                              <Loader
                                className={cn(
                                  "h-3.5 w-3.5 animate-spin",
                                  deletingInstances.has(instance.metadata.id)
                                    ? "text-gray-400"
                                    : "text-blue-400",
                                )}
                              />
                            </Button>
                          ) : (instance.status?.state === "ready" || instance.status?.state === "assigned") && (
                            <div className="flex gap-2">
                              <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      className="h-7 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 hover:border-blue-300"
                                      onClick={() =>
                                        setConnectDialog({
                                          isOpen: true,
                                          command: `lim connect ${instance.metadata.id.startsWith('ios') ? 'ios' : 'android'} ${instance.metadata.id}`,
                                        })
                                      }
                                    >
                                      <Terminal className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Connect locally</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      className="h-7 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 hover:border-blue-300"
                                      asChild
                                    >
                                      <Link to={`/stream/${instance.metadata.id}`}>
                                        <Gamepad2 className="h-3.5 w-3.5" />
                                      </Link>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Playground</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {instance.metadata.id}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <StatusBadge state={instance.status?.state ?? "unknown"} />
                        </TableCell>
                        {stateFilter?.includes("terminated") && (
                          <TableCell className="hidden md:table-cell">
                            {instance.metadata.terminatedAt &&
                            instance.metadata.createdAt
                              ? formatDuration(
                                  new Date(
                                    instance.metadata.terminatedAt,
                                  ).getTime() -
                                    new Date(
                                      instance.metadata.createdAt,
                                    ).getTime(),
                                )
                              : "N/A"}
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell">
                          {instance.metadata.createdAt
                            ? formatDuration(
                                (instance.metadata.terminatedAt
                                  ? new Date(instance.metadata.terminatedAt).getTime()
                                  : Date.now()) -
                                new Date(instance.metadata.createdAt).getTime(),
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {instance.metadata.createdAt
                            ? formatDateTime(instance.metadata.createdAt)
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {instance.status?.state !== "terminated" && (
                            <TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-200"
                                    onClick={() =>
                                      handleDelete(instance.metadata.id)
                                    }
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete instance</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
              {instancesData !== "loading" && (
                <CardFooter className="flex flex-col items-start gap-2">
                  <div className="text-xs text-muted-foreground">
                    Showing <strong>{allInstances.length}</strong> instances
                    {pageRef.current !== "loading" && pageRef.current.hasNextPage() && (
                      <span> (scroll down for more)</span>
                    )}
                  </div>
                  {isLoadingMore && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader className="h-3 w-3 animate-spin" />
                      Loading more instances...
                    </div>
                  )}
                </CardFooter>
              )}
            </Card>
          </Tabs>
        </main>
      </div>
      <ConnectDialog
        isOpen={connectDialog.isOpen}
        command={connectDialog.command}
        onOpenChange={(isOpen) => setConnectDialog({ isOpen })}
      />
    </div>
  );
}
